"use client";
/**
 * Client-side providers to access the initial context with hooks
 */

import React, { createContext, useSyncExternalStore, useCallback, useMemo } from "react";
import { DefaultInitialContext } from "../types";
import { __getContextCookie, __setContextCookie } from "./utils";
import * as Sentry from "@sentry/nextjs";

// Module-level promise for Suspense (CLIENT-ONLY, internal implementation detail)
let pendingPromise: Promise<DefaultInitialContext> | null = null;
let resolvePromise: ((value: DefaultInitialContext) => void) | null = null;

/**
 * Internal helper to get or create the pending promise for Suspense.
 * This is NOT exported publicly - only used by useInitialContext hook.
 */
function getOrCreatePendingPromise(): Promise<DefaultInitialContext> {
  // CRITICAL: Only create promise on client to avoid SSR issues
  if (typeof window === "undefined") {
    throw new Error("getOrCreatePendingPromise called on server - this is a bug");
  }

  if (!pendingPromise) {
    pendingPromise = new Promise((resolve) => {
      resolvePromise = resolve;
    });
  }
  return pendingPromise;
}

// Export for the hook to use (internal to SDK package, prefixed with underscore)
export { getOrCreatePendingPromise as _getPendingPromise };

export interface InitialContextState<T> {
  data: T | null;
  error: Error | null;
  setContextDangerously: (data: T) => void;
}

type InitialContextType = InitialContextState<DefaultInitialContext>;

export interface InitialContextProviderProps {
  children: React.ReactNode;
}

// Create the context with default values
const InitialContext = createContext<InitialContextType>({
  data: null,
  error: null,
  setContextDangerously: () => {},
});

export { InitialContext };

export const CONTEXT_CHANGE_EVENT = 'portalInitialContextChanged';

export function InitialContextProvider({ children }: InitialContextProviderProps) {
  const initialContext = useSyncExternalStore(
    // Subscribe to changes to InitialContext
    (callback) => {
      // Listen for changes via event
      window.addEventListener(CONTEXT_CHANGE_EVENT, callback);

      // Poll for changes every 10 seconds
      const pollingTimer = setInterval(callback, 10_000);

      return () => {
        window.removeEventListener(CONTEXT_CHANGE_EVENT, callback);
        clearInterval(pollingTimer);
      };
    },

    // Client-side, useSyncExternalStore allows us to read the window with hydration to get the snapshot
    () => {
      if (window.portalInitialContext) {
        // Resolve pending promise if it exists (for Suspense)
        if (resolvePromise) {
          resolvePromise(window.portalInitialContext);
          pendingPromise = null;
          resolvePromise = null;
        }

        console.log("[InitialContextProvider] window.portalInitialContext found", window.portalInitialContext);
        Sentry.addBreadcrumb({
          category: "initial-context",
          message: "window.portalInitialContext found",
          level: "info",
          data: {
            context: window.portalInitialContext ? "context found" : "context not found",
          }
        });
        return window.portalInitialContext;
      }

      // -- CI Only --
      // IC needs to be stored on session, as the window is blown away after a page reload
      // this happens when the test navigates from framer pages /paywall to /01-payment
      const debugOnlyContext = __getContextCookie();
      if (process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production' && debugOnlyContext) {
        // done this way to avoid circular references
        window.portalInitialContext = debugOnlyContext;
        return window.portalInitialContext;
      }

      // no context found
      Sentry.addBreadcrumb({
        category: "initial-context",
        message: "portalInitialContext not found",
        level: "info"
      });
      return null;
    },

    // Server-side, we do not want to return any data
    () => null
  );

  // Setter function that children can use to update context, should only be used in special cases (like debug bootstrapper)
  const setContextDangerously = useCallback((data: DefaultInitialContext) => {
    // Update InitialContext
    window.portalInitialContext = data;

    // Dispatch event to notify all subscribers
    window.dispatchEvent(new Event(CONTEXT_CHANGE_EVENT));

    console.info(
      `[InitialContextProvider] Context updated successfully`,
      data ? "Data exists" : "Data is null"
    );

    if (process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production' && data) {
      __setContextCookie(JSON.stringify(data));
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders of consumers
  // Without this, every provider re-render creates a new object, causing all consumers
  // with context in their useEffect deps to re-run their effects unnecessarily
  const contextValue = useMemo(
    () => ({ data: initialContext ?? null, error: null, setContextDangerously }),
    [initialContext, setContextDangerously]
  );

  return (
    <InitialContext.Provider value={contextValue}>
      {children}
    </InitialContext.Provider>
  );
}
